import { useState } from 'react'
import { motion } from 'framer-motion'
import { copyToClipboard } from '@/utils/helpers'
import Button from '@/components/common/Button'

export interface FENOutputProps {
  fen: string
  title?: string
}

const FENOutput = ({ fen, title = 'FEN Notation' }: FENOutputProps) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await copyToClipboard(fen)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-600 dark:text-dark-300 uppercase tracking-wider">
          {title}
        </h3>
      </div>

      <div className="relative group">
        <div className="bg-gray-50 dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-lg overflow-hidden">
          <div className="flex items-stretch">
            <pre className="flex-1 p-4 font-mono text-sm text-gray-800 dark:text-dark-50 overflow-x-auto hide-scrollbar">
              {fen}
            </pre>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className={`
                flex-shrink-0 rounded-none border-l border-gray-200 dark:border-dark-700
                ${copied ? 'bg-green-500/10 text-green-600 dark:text-green-400' : ''}
              `}
              aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
            >
              <motion.div
                key={copied ? 'check' : 'copy'}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
              >
                {copied ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <rect
                      x="9"
                      y="9"
                      width="13"
                      height="13"
                      rx="2"
                      ry="2"
                    />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                )}
              </motion.div>
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default FENOutput
